import React, { useMemo, useEffect } from 'react'
import { IndianRupee, Users } from 'lucide-react'
import ExcelDataGrid from '../common/ExcelDataGrid'

const SalaryDetailsTable = ({ salaryData, onSalaryDataChange }) => {

    // Define columns configuration
    const columns = useMemo(() => [
        { key: 'employeePan', label: 'PAN', width: '140px', type: 'text', required: true },
        { key: 'employeeName', label: 'Name', width: '200px', type: 'text', required: true },
        {
            key: 'category',
            label: 'Category',
            width: '120px',
            type: 'select',
            options: [
                { value: 'W', label: 'W - Woman' },
                { value: 'S', label: 'S - Senior Citizen' },
                { value: 'O', label: 'O - Super Senior Citizen' },
                { value: 'G', label: 'G - Others' }
            ]
        },
        { key: 'periodFrom', label: 'Period From', width: '130px', type: 'date' },
        { key: 'periodTo', label: 'Period To', width: '130px', type: 'date' },

        // Salary Structure
        { key: 'salary17_1', label: 'Salary as per Sec 17(1)', width: '130px', type: 'number' },
        { key: 'valPerq17_2', label: 'Value of Perquisites u/s 17(2)', width: '140px', type: 'number' },
        { key: 'profits17_3', label: 'Profits in lieu of salary u/s 17(3)', width: '140px', type: 'number' },
        { key: 'grossSalary', label: 'Total Gross Salary', width: '120px', type: 'number', readOnly: true },

        // Exemptions
        { key: 'hra', label: 'HRA Exemption u/s 10(13A)', width: '130px', type: 'number' },
        { key: 'travelConcession', label: 'Travel Concession u/s 10(5)', width: '130px', type: 'number' },
        { key: 'gratuity10_10', label: 'Gratuity Exemption u/s 10(10)', width: '140px', type: 'number' },
        { key: 'pension10_10A', label: 'Pension Exemption u/s 10(10A)', width: '140px', type: 'number' },
        { key: 'leaveEncashment10_10AA', label: 'Leave Encashment u/s 10(10AA)', width: '150px', type: 'number' },
        { key: 'specialAllowance10_14', label: 'Other special allowances u/s 10(14)', width: '200px', type: 'number' },
        { key: 'otherExemptions', label: 'Other Exemptions u/s 10', width: '130px', type: 'number' },
        { key: 'totalExemptions10', label: 'Total Exemptions u/s 10', width: '140px', type: 'number', readOnly: true },

        // Deductions
        { key: 'standardDed16ia', label: 'Standard Deduction u/s 16(ia)', width: '130px', type: 'number' },
        { key: 'entertainmentAllow16ii', label: 'Entertainment Allowance u/s 16(ii)', width: '140px', type: 'number' },
        { key: 'professionalTax16iii', label: 'Professional Tax u/s 16(iii)', width: '140px', type: 'number' },
        { key: 'deductions16', label: 'Total Deductions u/s 16', width: '130px', type: 'number', readOnly: true },
        { key: 'incomeChargeable', label: 'Income Chargeable (Salaries)', width: '140px', type: 'number', readOnly: true },
        { key: 'incomeOtherSources', label: 'Income from Other Sources', width: '130px', type: 'number' },
        { key: 'grossTotalIncome', label: 'Gross Total Income', width: '130px', type: 'number', readOnly: true },

        // Chapter VI-A (Granular Sections)
        { key: 'sec80C_Gross', label: '80C Gross', width: '100px', type: 'number' },
        { key: 'sec80C', label: '80C Deductible', width: '110px', type: 'number' },
        { key: 'sec80CCC_Gross', label: '80CCC Gross', width: '100px', type: 'number' },
        { key: 'sec80CCC', label: '80CCC Deductible', width: '110px', type: 'number' },
        { key: 'sec80CCD1_Gross', label: '80CCD(1) Gross', width: '110px', type: 'number' },
        { key: 'sec80CCD1', label: '80CCD(1) Ded', width: '110px', type: 'number' },
        { key: 'sec80CCG', label: '80CCG Deductible', width: '110px', type: 'number' },
        { key: 'sec80CCD1B_Gross', label: '80CCD(1B) Gross', width: '110px', type: 'number' },
        { key: 'sec80CCD1B', label: '80CCD(1B) Ded', width: '110px', type: 'number' },
        { key: 'sec80CCD2_Gross', label: '80CCD(2) Gross', width: '110px', type: 'number' },
        { key: 'sec80CCD2', label: '80CCD(2) Ded', width: '110px', type: 'number' },
        { key: 'sec80D_Gross', label: '80D Gross', width: '100px', type: 'number' },
        { key: 'sec80D', label: '80D Deductible', width: '110px', type: 'number' },
        { key: 'sec80E_Gross', label: '80E Gross', width: '100px', type: 'number' },
        { key: 'sec80E', label: '80E Deductible', width: '110px', type: 'number' },

        { key: 'sec80G_Gross', label: '80G Gross', width: '100px', type: 'number' },
        { key: 'sec80G_Qual', label: '80G Qualify', width: '110px', type: 'number' },
        { key: 'sec80G', label: '80G Deductible', width: '110px', type: 'number' },

        { key: 'sec80TTA_Gross', label: '80TTA Gross', width: '100px', type: 'number' },
        { key: 'sec80TTA_Qual', label: '80TTA Qualify', width: '110px', type: 'number' },
        { key: 'sec80TTA', label: '80TTA Deductible', width: '110px', type: 'number' },

        { key: 'sec80Others_Gross', label: 'Others Gross', width: '100px', type: 'number' },
        { key: 'sec80Others_Qual', label: 'Others Qualify', width: '110px', type: 'number' },
        { key: 'sec80Others', label: 'Others Deductible', width: '110px', type: 'number' },
        { key: 'chapterVIA', label: 'Total Chapter VI-A', width: '120px', type: 'number', readOnly: true },
        { key: 'taxableIncCurrent', label: 'Taxable Income (Current)', width: '140px', type: 'number', readOnly: true },
        { key: 'taxableAmtPrevEmp', label: 'Prev Employer Taxable Inc', width: '140px', type: 'number' },
        { key: 'totalTaxableIncome', label: 'Grand Total Taxable Income', width: '160px', type: 'number', readOnly: true },

        // Tax Calc
        { key: 'incomeTax', label: 'Income Tax', width: '120px', type: 'number' },
        { key: 'rebate87A', label: 'Rebate u/s 87A', width: '120px', type: 'number' },
        { key: 'surcharge', label: 'Surcharge', width: '100px', type: 'number' },
        { key: 'cess', label: 'Health & Edu Cess', width: '110px', type: 'number' },
        { key: 'relief89', label: 'Tax Relief u/s 89', width: '120px', type: 'number' },
        { key: 'netTaxPayable', label: 'Net Tax Payable', width: '120px', type: 'number', readOnly: true },

        // TDS
        { key: 'tdsCurrentEmp', label: 'TDS (Current Employer)', width: '130px', type: 'number' },
        { key: 'tdsPrevEmp', label: 'TDS (Prev Employer)', width: '130px', type: 'number' },
        { key: 'totalTdsDeducted', label: 'Total TDS Deducted', width: '130px', type: 'number', readOnly: true },
        { key: 'shortfallExcess', label: 'Shortfall/Excess Tax', width: '130px', type: 'number', readOnly: true },

        // Other Details
        {
            key: 'regime115BAC',
            label: 'Tax Regime (115BAC)',
            width: '110px',
            type: 'select',
            options: [
                { value: 'N', label: 'No' },
                { value: 'Y', label: 'Yes' }
            ]
        },
        { key: 'higherRateTax', label: 'Tax at Higher Rate?', width: '120px', type: 'select', options: [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }] },
        { key: 'rentExceedsThreshold', label: 'Rent Exceeds 1 Lakh?', width: '130px', type: 'select', options: [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }] },
        { key: 'landlordPan1', label: 'Landlord 1 PAN', width: '140px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordName1', label: 'Landlord 1 Name', width: '180px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordPan2', label: 'Landlord 2 PAN', width: '140px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordName2', label: 'Landlord 2 Name', width: '180px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordPan3', label: 'Landlord 3 PAN', width: '140px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordName3', label: 'Landlord 3 Name', width: '180px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordPan4', label: 'Landlord 4 PAN', width: '140px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'landlordName4', label: 'Landlord 4 Name', width: '180px', type: 'text', disabled: (r) => r.rentExceedsThreshold !== 'Y' },
        { key: 'interestPaidHouseProperty', label: 'Int Paid on House Loan?', width: '140px', type: 'select', options: [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }] },
        { key: 'lenderPan1', label: 'Lender 1 PAN', width: '140px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderName1', label: 'Lender 1 Name', width: '180px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderPan2', label: 'Lender 2 PAN', width: '140px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderName2', label: 'Lender 2 Name', width: '180px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderPan3', label: 'Lender 3 PAN', width: '140px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderName3', label: 'Lender 3 Name', width: '180px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderPan4', label: 'Lender 4 PAN', width: '140px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'lenderName4', label: 'Lender 4 Name', width: '180px', type: 'text', disabled: (r) => r.interestPaidHouseProperty !== 'Y' },
        { key: 'superannuationFundPaid', label: 'Superannuation Fund Contribution Paid?', width: '150px', type: 'select', options: [{ value: 'N', label: 'No' }, { value: 'Y', label: 'Yes' }] },
        { key: 'superannuationFundName', label: 'Superannuation Fund Name', width: '200px', type: 'text', disabled: (r) => r.superannuationFundPaid !== 'Y' },
        { key: 'superannuationDateFrom', label: 'Contribution Period From', width: '130px', type: 'date', disabled: (r) => r.superannuationFundPaid !== 'Y' },
        { key: 'superannuationDateTo', label: 'Contribution Period To', width: '130px', type: 'date', disabled: (r) => r.superannuationFundPaid !== 'Y' },
        { key: 'superannuationRepaidAmount', label: 'Repaid Contribution Amount', width: '140px', type: 'number', disabled: (r) => r.superannuationFundPaid !== 'Y' },
        { key: 'superannuationAvgRate', label: 'Average Rate of Tax (Past 3 Years)', width: '140px', type: 'number', disabled: (r) => r.superannuationFundPaid !== 'Y' },
        { key: 'superannuationTaxDeducted', label: 'Tax Deducted on Repayment', width: '140px', type: 'number', disabled: (r) => r.superannuationFundPaid !== 'Y' },
        { key: 'superannuationGrossTotal', label: 'Gross Total Income (Incl. Repayment)', width: '160px', type: 'number', readOnly: true },
        // Fields 80 & 81 - Final disclosures per Form 24Q Q4 spec
        { key: 'otherTdsTcs192_2B', label: 'Amount Reported u/s 192(2B) - Other TDS/TCS', width: '200px', type: 'number' }
    ], []);

    // Re-process data on initial load to clear stale superannuation values
    useEffect(() => {
        if (salaryData && salaryData.length > 0) {
            const hasStaleData = salaryData.some(row =>
                row.superannuationFundPaid === 'N' && parseFloat(row.superannuationGrossTotal || 0) !== 0
            );
            if (hasStaleData) {
                handleDataChange(salaryData);
            }
        }
    }, []);

    const handleDataChange = (newData) => {
        // Smart calculations for salary details
        const calculatedData = newData.map(row => {
            const r = { ...row };

            // 1. Gross Salary (17.1 + 17.2 + 17.3)
            r.grossSalary = parseFloat(r.salary17_1 || 0) + parseFloat(r.valPerq17_2 || 0) + parseFloat(r.profits17_3 || 0);

            // 2. Total Exemptions u/s 10
            r.totalExemptions10 = parseFloat(r.hra || 0) +
                parseFloat(r.travelConcession || 0) +
                parseFloat(r.gratuity10_10 || 0) +
                parseFloat(r.pension10_10A || 0) +
                parseFloat(r.leaveEncashment10_10AA || 0) +
                parseFloat(r.specialAllowance10_14 || 0) +
                parseFloat(r.otherExemptions || 0);

            // 3. Deductions u/s 16
            r.deductions16 = parseFloat(r.standardDed16ia || 0) +
                parseFloat(r.entertainmentAllow16ii || 0) +
                parseFloat(r.professionalTax16iii || 0);

            // 4. Income Chargeable under Salaries
            r.incomeChargeable = r.grossSalary - r.totalExemptions10 - r.deductions16;

            // 5. Gross Total Income
            r.grossTotalIncome = r.incomeChargeable + parseFloat(r.incomeOtherSources || 0);

            // Chapter VI-A Total = sum of all DEDUCTIBLE amounts only
            r.chapterVIA = parseFloat(r.sec80C || 0) +
                parseFloat(r.sec80CCC || 0) +
                parseFloat(r.sec80CCD1 || 0) +
                parseFloat(r.sec80CCG || 0) +
                parseFloat(r.sec80CCD1B || 0) +
                parseFloat(r.sec80CCD2 || 0) +
                parseFloat(r.sec80D || 0) +
                parseFloat(r.sec80E || 0) +
                parseFloat(r.sec80G || 0) +
                parseFloat(r.sec80TTA || 0) +
                parseFloat(r.sec80Others || 0);

            // 7. Total Taxable Income
            r.taxableIncCurrent = r.grossTotalIncome - r.chapterVIA;
            r.totalTaxableIncome = r.taxableIncCurrent + parseFloat(r.taxableAmtPrevEmp || 0);

            // 8. Net Tax Payable
            const totalTax = parseFloat(r.incomeTax || 0) + parseFloat(r.surcharge || 0) + parseFloat(r.cess || 0);
            const totalReliefs = parseFloat(r.rebate87A || 0) + parseFloat(r.relief89 || 0);
            r.netTaxPayable = totalTax - totalReliefs;

            // 9. Total TDS Deducted
            r.totalTdsDeducted = parseFloat(r.tdsCurrentEmp || 0) + parseFloat(r.tdsPrevEmp || 0);

            // 10. Shortfall/Excess
            r.shortfallExcess = r.netTaxPayable - r.totalTdsDeducted;

            // 11. Clear conditional fields if 'No' selected
            if (r.rentExceedsThreshold !== 'Y') {
                r.landlordPan1 = r.landlordName1 = r.landlordPan2 = r.landlordName2 = '';
                r.landlordPan3 = r.landlordName3 = r.landlordPan4 = r.landlordName4 = '';
            }
            if (r.interestPaidHouseProperty !== 'Y') {
                r.lenderPan1 = r.lenderName1 = r.lenderPan2 = r.lenderName2 = '';
                r.lenderPan3 = r.lenderName3 = r.lenderPan4 = r.lenderName4 = '';
            }
            if (r.superannuationFundPaid === 'N') {
                r.superannuationFundName = '';
                r.superannuationDateFrom = '';
                r.superannuationDateTo = '';
                r.superannuationRepaidAmount = '';
                r.superannuationAvgRate = '';
                r.superannuationTaxDeducted = '';
                r.superannuationGrossTotal = '';
            } else {
                r.superannuationGrossTotal = r.grossTotalIncome + parseFloat(r.superannuationRepaidAmount || 0);
            }

            return r;
        });

        onSalaryDataChange(calculatedData);
    };

    const handleRowAdd = () => {
        const newRow = {
            id: Date.now(),
            employeePan: '',
            employeeName: '',
            category: 'G',
            regime115BAC: 'N',
            gratuity10_10: 0,
            pension10_10A: 0,
            leaveEncashment10_10AA: 0,
            specialAllowance10_14: 0,
            standardDed16ia: 0,
            entertainmentAllow16ii: 0,
            professionalTax16iii: 0,
            deductions16: 0,
            sec80C_Gross: 0, sec80C: 0,
            sec80CCC_Gross: 0, sec80CCC: 0,
            sec80CCD1_Gross: 0, sec80CCD1: 0,
            sec80CCG: 0,
            sec80CCD1B_Gross: 0, sec80CCD1B: 0,
            sec80CCD2_Gross: 0, sec80CCD2: 0,
            sec80D_Gross: 0, sec80D: 0,
            sec80E_Gross: 0, sec80E: 0,
            sec80G_Gross: 0, sec80G_Qual: 0, sec80G: 0,
            sec80TTA_Gross: 0, sec80TTA_Qual: 0, sec80TTA: 0,
            sec80Others_Gross: 0, sec80Others_Qual: 0, sec80Others: 0,
            chapterVIA: 0,
            incomeChargeable: 0,
            incomeOtherSources: 0,
            grossTotalIncome: 0,
            taxableIncCurrent: 0,
            totalTaxableIncome: 0,
            taxableAmtPrevEmp: 0,
            tdsCurrentEmp: 0,
            tdsPrevEmp: 0,
            totalTdsDeducted: 0,
            otherTdsTcs192_2B: 0,
            higherRateTax: 'N',
            rentExceedsThreshold: 'N',
            landlordPan1: '', landlordName1: '',
            landlordPan2: '', landlordName2: '',
            landlordPan3: '', landlordName3: '',
            landlordPan4: '', landlordName4: '',
            interestPaidHouseProperty: 'N',
            lenderPan1: '', lenderName1: '',
            lenderPan2: '', lenderName2: '',
            lenderPan3: '', lenderName3: '',
            lenderPan4: '', lenderName4: '',
            superannuationFundPaid: 'N',
            superannuationFundName: '',
            superannuationDateFrom: '',
            superannuationDateTo: '',
            superannuationRepaidAmount: 0,
            superannuationAvgRate: 0,
            superannuationTaxDeducted: 0,
            superannuationGrossTotal: 0,
            // ... initialize other fields if necessary
        };
        onSalaryDataChange([...(salaryData || []), newRow]);
    };

    const handleRowDelete = (id) => {
        onSalaryDataChange((salaryData || []).filter(row => row.id !== id));
    };

    // Calculate Salary Totals
    const totalGrossSalary = (salaryData || []).reduce((acc, row) => acc + (parseFloat(row.grossSalary || 0) || 0), 0);
    const totalTaxableIncome = (salaryData || []).reduce((acc, row) => acc + (parseFloat(row.totalTaxableIncome || 0) || 0), 0);
    const totalTdsDeducted = (salaryData || []).reduce((acc, row) => acc + (parseFloat(row.totalTdsDeducted || 0) || 0), 0);

    const formatCurrency = (val) => {
        return Number(val || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="flex flex-col h-[calc(100vh-220px)] w-full max-w-full overflow-hidden">
            {/* Salary KPI Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 flex-shrink-0">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-200/80 rounded-xl p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">Total Gross Salary</span>
                        <span className="p-1 bg-blue-100 text-blue-700 rounded-md text-xs font-bold flex items-center justify-center">
                            <IndianRupee className="w-3.5 h-3.5" />
                        </span>
                    </div>
                    <div className="mt-1.5 text-xl font-bold text-blue-900">
                        ₹{formatCurrency(totalGrossSalary)}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Across {(salaryData || []).length} employees</div>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-purple-50/60 border border-indigo-200/80 rounded-xl p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-indigo-700">Total Taxable Income</span>
                        <span className="p-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-bold flex items-center justify-center">
                            <IndianRupee className="w-3.5 h-3.5" />
                        </span>
                    </div>
                    <div className="mt-1.5 text-xl font-bold text-indigo-900">
                        ₹{formatCurrency(totalTaxableIncome)}
                    </div>
                    <div className="text-[11px] text-indigo-700 mt-0.5">Chargeable salary income</div>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-teal-50/60 border border-emerald-200/80 rounded-xl p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Total TDS Deducted</span>
                        <span className="p-1 bg-emerald-100 text-emerald-700 rounded-md text-xs font-bold flex items-center justify-center">
                            <IndianRupee className="w-3.5 h-3.5" />
                        </span>
                    </div>
                    <div className="mt-1.5 text-xl font-bold text-emerald-900">
                        ₹{formatCurrency(totalTdsDeducted)}
                    </div>
                    <div className="text-[11px] text-emerald-700 mt-0.5">Total salary TDS</div>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-gray-50 border border-gray-200 rounded-xl p-3 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">Total Employees</span>
                        <span className="p-1 bg-gray-200 text-gray-700 rounded-md text-xs font-bold flex items-center justify-center">
                            <Users className="w-3.5 h-3.5" />
                        </span>
                    </div>
                    <div className="mt-1.5 text-xl font-bold text-gray-900">
                        {(salaryData || []).length}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Annexure II records</div>
                </div>
            </div>

            <div className="flex-1 min-h-0 w-full overflow-hidden">
                <ExcelDataGrid
                    columns={columns}
                    data={salaryData || []}
                    onDataChange={handleDataChange}
                    onRowAdd={handleRowAdd}
                    onRowDelete={handleRowDelete}
                    height="100%"
                    stickyColumns={2}
                    summaryRow={(salaryData && salaryData.length > 0) ? {
                        employeePan: <span className="font-bold text-gray-800">TOTAL:</span>,
                        grossSalary: <span className="font-bold text-blue-700">₹{formatCurrency(totalGrossSalary)}</span>,
                        totalTaxableIncome: <span className="font-bold text-indigo-700">₹{formatCurrency(totalTaxableIncome)}</span>,
                        totalTdsDeducted: <span className="font-bold text-emerald-700">₹{formatCurrency(totalTdsDeducted)}</span>
                    } : null}
                />
            </div>
        </div>
    );
}

export default SalaryDetailsTable
